import { useGLFlags } from '@group-link-one/gl-utils';

type Pages = {
  reports: boolean;
  api: boolean;
  groups: boolean;
  deviceBlocks: boolean;
  tagsManagement: boolean;
  settings: {
    overview: boolean;
    myAccount: boolean;
    loginSecurity: boolean;
    users: boolean;
    audit: boolean;
  };
  developer: {
    sdk: boolean;
    batch: boolean;
    credentials: boolean;
  };
  fallback: {
    showMaintenanceMode: boolean;
    showInstabilityMode: boolean;
  };
};

export type Features = {
  impersonation: boolean;
  api: {
    showTotalSDKsIds: boolean;
  };
  groups: {
    createUserGroup: boolean;
  };
  users: {
    showBtnCreate: boolean;
    showBtnChangeRole: boolean;
    showBtnSetCustomRole: boolean;
    showBtnSetAdminRole: boolean;
    showBtnSetMemberRole: boolean;
    showBtnActivateUser: boolean;
    showRoleCardRoot: boolean;
    showBtnDeleteInvite: boolean;
    showBtnDeactivatedUser: boolean;
    showFilterSearch: boolean;
    showBtnUpdateRole: boolean;
  };
  developer: {
    sdk: {
      showIssuedBy: boolean;
      shouldUseTokenV2: boolean;
    };
  };
  reports: {
    filters: {
      button: boolean;
    };
  };
  tagsManagement: {
    batchOption: boolean;
    importHistory: boolean;
    historyDownload: boolean;
  };
};

type Mocks = {
  useMocks: boolean;
  services: {
    projections: boolean;
  };
};

interface UsePrivileges {
  pages: Pages;
  features: Features;
  mocks: Mocks;
}

export const usePrivileges = (): UsePrivileges => {
  const { showFeature } = useGLFlags();

  const pages: Pages = {
    reports: showFeature('show_report_page'),
    api: showFeature('show_api_page'),
    groups: showFeature('show_groups_page'),
    deviceBlocks: showFeature('show_device_blocks_page'),
    tagsManagement: showFeature('show_tags_management_page'),
    settings: {
      overview: showFeature('show_settings_overview_page'),
      myAccount: showFeature('show_settings_my_account_page'),
      loginSecurity: showFeature('show_settings_login_security_page'),
      users: showFeature('show_settings_users_page'),
      audit: showFeature('show_settings_audit_page'),
    },
    developer: {
      sdk: showFeature('developer_sdk_page'),
      batch: showFeature('developer_batch_page'),
      credentials: showFeature('developer_credentials_page'),
    },
    fallback: {
      showMaintenanceMode: showFeature('maintenance_page'),
      showInstabilityMode: showFeature('instability_page'),
    },
  };

  const features: Features = {
    impersonation: showFeature('action_btn_impersonate_mode'),
    api: {
      showTotalSDKsIds: showFeature('show_total_sdks_ids'),
    },
    groups: {
      createUserGroup: showFeature('show_btn_create_user_group'),
    },
    users: {
      showBtnCreate: showFeature('show_btn_create_user'),
      showBtnChangeRole: showFeature('show_btn_change_role'),
      showBtnSetCustomRole: showFeature('show_btn_custom_role'),
      showBtnSetAdminRole: showFeature('show_btn_set_admin_role'),
      showBtnSetMemberRole: showFeature('show_btn_set_member_role'),
      showBtnActivateUser: showFeature('show_btn_activate_user'),
      showRoleCardRoot: showFeature('show_role_card_root'),
      showBtnDeleteInvite: showFeature('show_btn_delete_invite'),
      showBtnDeactivatedUser: showFeature('show_btn_deactivated_user'),
      showFilterSearch: showFeature('show_filter_search_users'),
      showBtnUpdateRole: showFeature('show_btn_update_role'),
    },
    developer: {
      sdk: {
        showIssuedBy: showFeature('developer_sdk_show_issued_by'),
        shouldUseTokenV2: showFeature('developer_sdk_use_token_v2'),
      },
    },
    reports: {
      filters: {
        button: showFeature('reports_filter_button'),
      },
    },
    tagsManagement: {
      batchOption: showFeature('tags_management_batch_option'),
      importHistory: showFeature('tags_management_import_history'),
      historyDownload: showFeature('tags_management_history_card_download'),
    },
  };

  const mocks: Mocks = {
    useMocks: showFeature('use_mocks'),
    services: {
      projections: showFeature('use_mocks_projections'),
    },
  };

  return {
    pages,
    features,
    mocks,
  };
};
